import React from 'react';
import { Clock, Calendar } from 'lucide-react';
import { type Intervention, CONNECTION_TYPE_COLORS, STATUS_COLORS } from '@/lib/data';

interface Props {
  interventions: Intervention[];
  formatDate: (d: string) => string;
  formatDuration: (min: number) => string;
}

export default function ReportTable({ interventions, formatDate, formatDuration }: Props) {
  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
        <h3 className="font-semibold text-base" style={{ color: 'hsl(var(--foreground))' }}>
          Detaljan spisak intervencija
        </h3>
        <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
          Sortirano od najnovijeg ka najstarijem
        </p>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[760px]">
          <thead style={{ backgroundColor: 'hsl(var(--muted) / 0.6)', borderBottom: '1px solid hsl(var(--border))' }}>
            <tr>
              <th className="table-header">#</th>
              <th className="table-header">Datum i vreme</th>
              <th className="table-header">Klijent</th>
              <th className="table-header">Tip veze</th>
              <th className="table-header">Opis intervencije</th>
              <th className="table-header">Trajanje</th>
              <th className="table-header">Oprema</th>
              <th className="table-header">Status</th>
            </tr>
          </thead>
          <tbody>
            {interventions.map((inv, idx) => {
              const ctColor = CONNECTION_TYPE_COLORS[inv.connectionType];
              const stColor = STATUS_COLORS[inv.status];
              return (
                <tr
                  key={inv.id}
                  className="table-row"
                  style={idx % 2 === 1 ? { backgroundColor: 'hsl(var(--muted) / 0.25)' } : {}}
                >
                  <td className="table-cell">
                    <span
                      className="text-xs font-mono tabular-nums"
                      style={{ color: 'hsl(var(--muted-foreground))' }}
                    >
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={12} style={{ color: 'hsl(var(--muted-foreground))' }} />
                      <div>
                        <p className="text-sm tabular-nums font-medium">
                          {formatDate(inv.date)}
                        </p>
                        <p className="text-xs tabular-nums" style={{ color: 'hsl(var(--muted-foreground))' }}>
                          {inv.time}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <span className="text-sm font-medium">{inv.clientName}</span>
                  </td>
                  <td className="table-cell">
                    <span className={`badge ${ctColor.bg} ${ctColor.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${ctColor.dot}`} />
                      {inv.connectionType}
                    </span>
                  </td>
                  <td className="table-cell max-w-[280px]">
                    <p className="text-sm leading-snug line-clamp-3" title={inv.description}>
                      {inv.description}
                    </p>
                  </td>
                  <td className="table-cell">
                    <span className="flex items-center gap-1 text-sm tabular-nums whitespace-nowrap">
                      <Clock size={12} style={{ color: 'hsl(var(--muted-foreground))' }} />
                      {formatDuration(inv.duration)}
                    </span>
                  </td>
                  <td className="table-cell">
                    {inv.equipmentLabel ? (
                      <span
                        className="text-xs px-2 py-0.5 rounded-md"
                        style={{ backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
                      >
                        {inv.equipmentLabel}
                      </span>
                    ) : (
                      <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>—</span>
                    )}
                  </td>
                  <td className="table-cell">
                    <span className={`badge ${stColor.bg} ${stColor.text}`}>
                      {inv.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}